import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { intake } from '@lawbee/domain';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  ErrorState,
  Field,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Textarea,
  cn,
  segmentedItemClass,
  segmentedListClass,
  useToast,
} from '@lawbee/ui';
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, Copy, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { IntakeRenderer, PARTY_ROLES } from '../../../components/IntakeRenderer';
import { ApiError, api, errorMessage, isApiError } from '../../../lib/api';
import { useCopy, useParam } from '../../../lib/hooks';
import type { IntakeField, IntakeFormDetail } from '../../../lib/types';
import { slugKey } from './shared';

type FieldType = IntakeField['type'];
type ShowIf = NonNullable<IntakeField['showIf']>;
type Item = { uid: string; field: IntakeField; keyTouched: boolean };

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Short answer',
  textarea: 'Paragraph',
  email: 'Email',
  phone: 'Phone',
  date: 'Date',
  number: 'Number',
  select: 'Dropdown (one choice)',
  multiselect: 'Checkboxes (several choices)',
  checkbox: 'Single checkbox (yes / no)',
  file: 'File upload',
  parties: 'People and companies (conflict check)',
};

const TYPE_OPTIONS = intake.FIELD_TYPES.map((t) => ({ value: t, label: FIELD_TYPE_LABELS[t] ?? t }));

let uidCounter = 0;
const nextUid = () => `f${++uidCounter}`;

export function IntakeFormBuilderPage() {
  const id = useParam('id');
  const q = useQuery({ queryKey: ['intake-form', id], queryFn: () => api.get<{ form: IntakeFormDetail }>(`/api/intake/forms/${id}`), enabled: !!id });
  if (q.isLoading) return <LoadingState label="Loading form" />;
  if (q.error || !q.data) {
    return (
      <div>
        <PageHeader title="Intake form" breadcrumbs={<Link to="/intake-forms">Intake forms</Link>} />
        <ErrorState title={isApiError(q.error, 'not_found') ? 'Form not found' : 'Could not load this form'} error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  }
  return <Builder key={q.data.form.id} form={q.data.form} />;
}

function cleanField(f: IntakeField): IntakeField {
  const out: IntakeField = { key: f.key.trim(), label: f.label.trim(), type: f.type };
  if (f.required) out.required = true;
  if (f.help?.trim()) out.help = f.help.trim();
  if ((f.type === 'select' || f.type === 'multiselect') && f.options) out.options = f.options.map((o) => o.trim()).filter(Boolean);
  if (f.type === 'parties' && f.partyRole) out.partyRole = f.partyRole;
  if (f.showIf?.field) out.showIf = f.showIf;
  return out;
}

function Builder({ form }: { form: IntakeFormDetail }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { copy, copied } = useCopy();
  const [name, setName] = useState(form.name);
  const [intro, setIntro] = useState(form.intro ?? '');
  const [active, setActive] = useState(form.active);
  const [items, setItems] = useState<Item[]>(() => form.fields.map((f) => ({ uid: nextUid(), field: f, keyTouched: true })));
  const [openUid, setOpenUid] = useState<string | null>(null);
  const [view, setView] = useState<'edit' | 'preview'>('edit');
  const [saved, setSaved] = useState(() => JSON.stringify({ name: form.name, intro: form.intro ?? '', active: form.active, fields: form.fields.map(cleanField) }));
  const [serverProblems, setServerProblems] = useState<string[]>([]);

  const fields = useMemo(() => items.map((i) => cleanField(i.field)), [items]);
  const problems = useMemo(() => intake.validateFormDefinition(fields), [fields]);
  const current = JSON.stringify({ name, intro, active, fields });
  const dirty = current !== saved;

  const save = useMutation({
    mutationFn: () => api.patch<{ form: IntakeFormDetail }>(`/api/intake/forms/${form.id}`, { name: name.trim(), intro: intro.trim() || null, fields, active }),
    onSuccess: async () => {
      setSaved(current);
      setServerProblems([]);
      await Promise.all([qc.invalidateQueries({ queryKey: ['intake-forms'] }), qc.invalidateQueries({ queryKey: ['intake-form', form.id] })]);
      toast.success('Form saved');
    },
    onError: (err) => {
      setServerProblems(detailMessages(err));
      toast.error('Could not save the form', errorMessage(err));
    },
  });

  const update = (uid: string, patch: Partial<IntakeField>, opts?: { keyTouched?: boolean }) =>
    setItems((list) =>
      list.map((it) => {
        if (it.uid !== uid) return it;
        const field = { ...it.field, ...patch };
        const keyTouched = opts?.keyTouched ?? it.keyTouched;
        if (patch.label !== undefined && !keyTouched) field.key = uniqueKey(slugKey(patch.label), list, uid);
        return { ...it, field, keyTouched };
      }),
    );
  const move = (uid: string, dir: -1 | 1) =>
    setItems((list) => {
      const i = list.findIndex((x) => x.uid === uid);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= list.length) return list;
      const next = [...list];
      const a = next[i]!;
      next[i] = next[j]!;
      next[j] = a;
      return next;
    });
  const remove = (uid: string) => setItems((list) => list.filter((x) => x.uid !== uid));
  const add = () => {
    const uid = nextUid();
    setItems((list) => [...list, { uid, field: { key: uniqueKey('new_question', list, uid), label: '', type: 'text' }, keyTouched: false }]);
    setOpenUid(uid);
  };

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Link to="/intake-forms" className="hover:text-foreground">
            Intake forms
          </Link>
        }
        title={name || 'Untitled form'}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {active ? <Badge variant="success">Active</Badge> : <Badge>Inactive</Badge>}
            {dirty ? <Badge variant="warning">Unsaved changes</Badge> : <span className="text-[13px]">All changes saved</span>}
          </span>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => void copy(form.publicUrl, 'Link copied')}>
              {copied === form.publicUrl ? <Check aria-hidden /> : <Copy aria-hidden />} Copy link
            </Button>
            <Button asChild variant="outline">
              <a href={form.publicUrl} target="_blank" rel="noreferrer">
                <ExternalLink aria-hidden /> Open
              </a>
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty || problems.length > 0 || name.trim().length < 2}>
              Save
            </Button>
          </>
        }
      />

      <div className="mb-4 lg:hidden">
        <div role="tablist" aria-label="Builder view" className={segmentedListClass}>
          <button type="button" role="tab" aria-selected={view === 'edit'} className={segmentedItemClass(view === 'edit')} onClick={() => setView('edit')}>
            Edit
          </button>
          <button type="button" role="tab" aria-selected={view === 'preview'} className={segmentedItemClass(view === 'preview')} onClick={() => setView('preview')}>
            Preview
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <div className={cn('min-w-0 space-y-5', view === 'preview' && 'hidden lg:block')}>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <Field label="Form name" htmlFor="fb-name" required error={name.trim().length < 2 ? 'At least 2 characters.' : undefined}>
                <Input id="fb-name" value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Introduction" htmlFor="fb-intro" description="Shown above the questions.">
                <Textarea id="fb-intro" rows={3} value={intro} onChange={(e) => setIntro(e.target.value)} />
              </Field>
              <Checkbox label="Accept submissions (form is active)" checked={active} onCheckedChange={(c) => setActive(c === true)} />
              <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-muted-foreground">{form.publicUrl}</span>
              </div>
            </CardContent>
          </Card>

          <Alert variant="info" title="Contact details are always asked first">
            First name, last name, email and phone are built in. Add the questions specific to this kind of matter below.
          </Alert>

          {problems.length > 0 || serverProblems.length > 0 ? (
            <Alert variant="destructive" title="Fix these before saving">
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {[...new Set([...problems, ...serverProblems])].map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Alert>
          ) : null}

          <ol className="space-y-3" aria-label="Questions">
            {items.map((it, index) => (
              <FieldEditor
                key={it.uid}
                item={it}
                index={index}
                count={items.length}
                earlier={items.slice(0, index).map((x) => x.field)}
                open={openUid === it.uid}
                onToggle={() => setOpenUid(openUid === it.uid ? null : it.uid)}
                onChange={(patch, opts) => update(it.uid, patch, opts)}
                onMove={(dir) => move(it.uid, dir)}
                onRemove={() => remove(it.uid)}
              />
            ))}
          </ol>
          {items.length === 0 ? <p className="rounded-xl bg-muted/50 px-4 py-8 text-center text-sm text-muted-foreground">No questions yet. Add the first one.</p> : null}
          <Button variant="outline" onClick={add}>
            <Plus aria-hidden /> Add field
          </Button>
        </div>

        <div className={cn('min-w-0', view === 'edit' && 'hidden lg:block')}>
          <div className="lg:sticky lg:top-6">
            <Card>
              <CardContent className="pt-6">
                <p className="mb-1 text-[12px] font-medium uppercase tracking-wide text-muted-foreground">Live preview</p>
                <h2 className="text-xl font-semibold">{name || 'Untitled form'}</h2>
                {intro ? <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{intro}</p> : null}
                <div className="mt-5">
                  <IntakeRenderer key={JSON.stringify(fields)} fields={fields} preview />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function detailMessages(err: unknown): string[] {
  if (!(err instanceof ApiError) || !Array.isArray(err.details)) return err instanceof ApiError ? [err.message] : [];
  return (err.details as unknown[])
    .map((d) => {
      if (typeof d === 'string') return d;
      if (d && typeof d === 'object') {
        const o = d as { path?: unknown; message?: unknown };
        const path = Array.isArray(o.path) ? o.path.join('.') : typeof o.path === 'string' ? o.path : '';
        return `${path ? `${path}: ` : ''}${typeof o.message === 'string' ? o.message : ''}`;
      }
      return '';
    })
    .filter(Boolean);
}

function uniqueKey(base: string, list: Item[], selfUid: string): string {
  const taken = new Set(list.filter((x) => x.uid !== selfUid).map((x) => x.field.key));
  for (const k of intake.CORE_FIELDS) taken.add(k.key);
  let key = base;
  for (let i = 2; taken.has(key); i++) key = `${base}_${i}`;
  return key;
}

function FieldEditor({
  item,
  index,
  count,
  earlier,
  open,
  onToggle,
  onChange,
  onMove,
  onRemove,
}: {
  item: Item;
  index: number;
  count: number;
  earlier: IntakeField[];
  open: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<IntakeField>, opts?: { keyTouched?: boolean }) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const f = item.field;
  const pid = `fe-${item.uid}`;
  const hasOptions = f.type === 'select' || f.type === 'multiselect';
  const [optionsText, setOptionsText] = useState((f.options ?? []).join('\n'));

  return (
    <li className="rounded-xl bg-card shadow-card">
      <div className="flex items-center gap-2 p-3 pl-4">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={open} aria-controls={`${pid}-body`} onClick={onToggle}>
          {open ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
          <span className="figures w-5 shrink-0 text-[12px] text-muted-foreground">{index + 1}</span>
          <span className="min-w-0 flex-1">
            <span className={cn('block truncate text-sm font-medium', !f.label && 'italic text-muted-foreground')}>
              {f.label || 'Untitled question'}
              {f.required ? <span className="text-destructive"> *</span> : null}
            </span>
            <span className="block truncate text-[12px] text-muted-foreground">
              {FIELD_TYPE_LABELS[f.type]}
              {f.showIf ? ' · conditional' : ''}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center">
          <Button variant="ghost" size="icon" className="size-8" aria-label="Move up" disabled={index === 0} onClick={() => onMove(-1)}>
            <ArrowUp />
          </Button>
          <Button variant="ghost" size="icon" className="size-8" aria-label="Move down" disabled={index === count - 1} onClick={() => onMove(1)}>
            <ArrowDown />
          </Button>
          <Button variant="ghost" size="icon" className="size-8 text-destructive" aria-label={`Remove ${f.label || 'question'}`} onClick={onRemove}>
            <Trash2 />
          </Button>
        </div>
      </div>
      {open ? (
        <div id={`${pid}-body`} className="space-y-4 border-t px-4 pb-4 pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Question" htmlFor={`${pid}-label`} required className="sm:col-span-2">
              <Input id={`${pid}-label`} value={f.label} onChange={(e) => onChange({ label: e.target.value })} autoFocus={!f.label} />
            </Field>
            <Field label="Answer type" htmlFor={`${pid}-type`}>
              <Select
                id={`${pid}-type`}
                value={f.type}
                options={TYPE_OPTIONS}
                onChange={(e) => {
                  const type = e.target.value as FieldType;
                  onChange({ type, ...(type === 'parties' && !f.partyRole ? { partyRole: 'opposing_party' } : {}) });
                }}
              />
            </Field>
            <Field label="Field key" htmlFor={`${pid}-key`} description="Lowercase letters, digits and underscores.">
              <Input
                id={`${pid}-key`}
                className="font-mono text-[13px]"
                value={f.key}
                onChange={(e) => onChange({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }, { keyTouched: true })}
              />
            </Field>
          </div>
          <Field label="Help text" htmlFor={`${pid}-help`}>
            <Input id={`${pid}-help`} value={f.help ?? ''} onChange={(e) => onChange({ help: e.target.value })} />
          </Field>
          {hasOptions ? (
            <Field label="Options" htmlFor={`${pid}-options`} description="One option per line." required>
              <Textarea
                id={`${pid}-options`}
                rows={4}
                value={optionsText}
                onChange={(e) => {
                  setOptionsText(e.target.value);
                  onChange({ options: e.target.value.split('\n').map((o) => o.trim()).filter(Boolean) });
                }}
              />
            </Field>
          ) : null}
          {f.type === 'parties' ? (
            <Field label="Default role for people listed" htmlFor={`${pid}-role`}>
              <Select id={`${pid}-role`} value={f.partyRole ?? 'opposing_party'} options={PARTY_ROLES} onChange={(e) => onChange({ partyRole: e.target.value })} />
            </Field>
          ) : null}
          <Checkbox
            label={f.type === 'checkbox' ? 'Required (a checkbox is never flagged as missing)' : 'Required (flagged for follow-up when left blank)'}
            checked={!!f.required}
            onCheckedChange={(c) => onChange({ required: c === true })}
          />
          <ShowIfEditor pid={pid} field={f} earlier={earlier} onChange={(showIf) => onChange({ showIf })} />
        </div>
      ) : null}
    </li>
  );
}

type Operator = 'equals' | 'in' | 'notEmpty';

function operatorOf(s: ShowIf | undefined): Operator {
  if (!s) return 'equals';
  if (s.notEmpty) return 'notEmpty';
  if (s.in) return 'in';
  return 'equals';
}

function ShowIfEditor({ pid, field, earlier, onChange }: { pid: string; field: IntakeField; earlier: IntakeField[]; onChange: (showIf: ShowIf | undefined) => void }) {
  const s = field.showIf;
  const parents = [...intake.CORE_FIELDS, ...earlier].filter((p) => p.key && p.key !== field.key && p.type !== 'file');
  const parent = s ? parents.find((p) => p.key === s.field) ?? [...intake.CORE_FIELDS, ...earlier].find((p) => p.key === s.field) : undefined;
  const op = operatorOf(s);
  const enabled = !!s;
  const parentOptions = parent && (parent.type === 'select' || parent.type === 'multiselect') ? parent.options ?? [] : null;

  const setParent = (key: string) => {
    const p = parents.find((x) => x.key === key);
    if (!p) return;
    if (p.type === 'checkbox') onChange({ field: key, equals: true });
    else if ((p.type === 'select' || p.type === 'multiselect') && p.options?.[0]) onChange({ field: key, equals: p.options[0] });
    else onChange({ field: key, notEmpty: true });
  };
  const setOp = (next: Operator) => {
    if (!s) return;
    if (next === 'notEmpty') onChange({ field: s.field, notEmpty: true });
    else if (next === 'in') onChange({ field: s.field, in: parentOptions?.[0] ? [parentOptions[0]] : [] });
    else onChange({ field: s.field, equals: parent?.type === 'checkbox' ? true : parentOptions?.[0] ?? '' });
  };

  return (
    <div className="space-y-3 rounded-lg bg-muted/50 p-3">
      <Checkbox
        label="Only show this question when…"
        checked={enabled}
        disabled={parents.length === 0}
        onCheckedChange={(c) => {
          if (c !== true) onChange(undefined);
          else if (parents[0]) setParent(parents[parents.length - 1]!.key);
        }}
      />
      {enabled && s ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Question" htmlFor={`${pid}-sif`}>
            <Select
              id={`${pid}-sif`}
              value={s.field}
              onChange={(e) => setParent(e.target.value)}
              options={[
                ...(parents.some((p) => p.key === s.field) ? [] : [{ value: s.field, label: `${s.field} (missing)` }]),
                ...parents.map((p) => ({ value: p.key, label: p.label || p.key })),
              ]}
            />
          </Field>
          <Field label="Condition" htmlFor={`${pid}-sio`}>
            <Select
              id={`${pid}-sio`}
              value={op}
              onChange={(e) => setOp(e.target.value as Operator)}
              options={[
                { value: 'equals', label: 'equals' },
                ...(parent?.type === 'checkbox' ? [] : [{ value: 'in', label: 'is one of' }]),
                { value: 'notEmpty', label: 'is answered (not empty)' },
              ]}
            />
          </Field>
          {op === 'equals' ? (
            <Field label="Value" htmlFor={`${pid}-siv`} className="sm:col-span-2">
              {parent?.type === 'checkbox' ? (
                <Select
                  id={`${pid}-siv`}
                  value={s.equals === false ? 'false' : 'true'}
                  onChange={(e) => onChange({ field: s.field, equals: e.target.value === 'true' })}
                  options={[
                    { value: 'true', label: 'Checked (yes)' },
                    { value: 'false', label: 'Not checked (no)' },
                  ]}
                />
              ) : parentOptions ? (
                <Select id={`${pid}-siv`} value={typeof s.equals === 'string' ? s.equals : ''} placeholder="Choose…" onChange={(e) => onChange({ field: s.field, equals: e.target.value })} options={parentOptions.map((o) => ({ value: o, label: o }))} />
              ) : (
                <Input id={`${pid}-siv`} value={typeof s.equals === 'string' ? s.equals : ''} onChange={(e) => onChange({ field: s.field, equals: e.target.value })} />
              )}
            </Field>
          ) : null}
          {op === 'in' ? (
            parentOptions ? (
              <fieldset className="space-y-2 sm:col-span-2">
                <legend className="text-[13px] font-medium">Any of these answers</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {parentOptions.map((o) => (
                    <Checkbox
                      key={o}
                      label={o}
                      checked={(s.in ?? []).includes(o)}
                      onCheckedChange={(c) => onChange({ field: s.field, in: c === true ? [...(s.in ?? []), o] : (s.in ?? []).filter((x) => x !== o) })}
                    />
                  ))}
                </div>
              </fieldset>
            ) : (
              <Field label="Values" htmlFor={`${pid}-sivs`} description="Separate values with commas." className="sm:col-span-2">
                <Input
                  id={`${pid}-sivs`}
                  defaultValue={(s.in ?? []).join(', ')}
                  onChange={(e) => onChange({ field: s.field, in: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                />
              </Field>
            )
          ) : null}
        </div>
      ) : parents.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">Add an earlier question to make this one conditional.</p>
      ) : null}
    </div>
  );
}
